import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import { format, subYears } from 'date-fns'
import {
  downloadBlob, truncateString, formatCpf, formatCurrencyInt,
} from '@core/utils/utils'

export default function useMonthlyProfessionalFollowUpReport() {
  const now = new Date()
  const currentYear = format(now, 'yyyy')
  const currentMonth = format(now, 'MM')

  const filterYear = ref(currentYear)
  const filterMonth = ref(currentMonth)

  const loading = ref(false)
  const items = ref([])

  const yearOptions = (new Array(4)).fill(0).map((_, i) => {
    const year = format(subYears(now, i), 'yyyy')
    return { label: year, value: year }
  }).filter(({ value }) => value > '2020')

  const allMonthOptions = [
    { label: 'Janeiro', value: '01' },
    { label: 'Fevereiro', value: '02' },
    { label: 'Março', value: '03' },
    { label: 'Abril', value: '04' },
    { label: 'Maio', value: '05' },
    { label: 'Junho', value: '06' },
    { label: 'Julho', value: '07' },
    { label: 'Agosto', value: '08' },
    { label: 'Setembro', value: '09' },
    { label: 'Outubro', value: '10' },
    { label: 'Novembro', value: '11' },
    { label: 'Dezembro', value: '12' },
  ]

  const monthOptions = computed(() => (
    filterYear.value === currentYear
      ? allMonthOptions.filter(({ value }) => value <= currentMonth)
      : allMonthOptions
  ))

  watch(filterYear, value => {
    if (value === currentYear && filterMonth.value > currentMonth) {
      filterMonth.value = currentMonth
    }
  })

  const formatFollowUpDate = v => (v ? format(new Date(v), 'dd/MM/yy HH:mm') : '-')
  const formatCompetence = c => {
    const [year, month] = c.split('-')
    return `${month}/${year}`
  }

  const tableColumns = [
    { key: 'physician_name', label: 'Profissional' },
    { key: 'crm_registrations', label: 'Registro' },
    { key: 'phone', label: 'Contato' },
    { key: 'status', label: 'Status' },
    {
      key: 'general_first_advance_at', label: '1ª Antecipação Geral', formatter: formatFollowUpDate,
    },
    {
      key: 'general_last_advance_at', label: 'Última Antecipação Geral', formatter: formatFollowUpDate,
    },
    { key: 'launched_current_month', label: 'Lançamentos Mês Atual' },
    { key: 'anticipation_current_month', label: 'Antec. Mês Atual' },
    { key: 'anticipation_previous_month', label: 'Antec. Mês Anterior' },
    { key: 'anticipation_two_months_ago', label: 'Antec. Mês Retrasado' },
    {
      key: 'new_registration_at', label: 'Data de Cadastro', formatter: formatFollowUpDate,
    },
  ]

  const getClassificationColor = color => {
    const colors = {
      red: '#df7273',
      yellow: '#ffb976',
      green: '#48da89',
    }
    return colors[color]
  }

  const getParams = () => ({
    competence: `${filterYear.value}-${filterMonth.value}`,
  })

  const loadReport = () => {
    loading.value = true
    store.dispatch('app-reports/monthlyProfessionalFollowUpReport', getParams())
      .then(response => {
        items.value = response.data.data ?? []
      })
      .finally(() => {
        loading.value = false
      })
  }

  const exporting = ref(false)
  const downloadReport = () => {
    exporting.value = true
    store.dispatch('app-reports/monthlyProfessionalFollowUpReportExport', getParams())
      .then(response => {
        downloadBlob(null, response)
      })
      .finally(() => {
        exporting.value = false
      })
  }

  return {
    loading,
    filterYear,
    filterMonth,
    yearOptions,
    monthOptions,
    items,
    tableColumns,
    getClassificationColor,
    loadReport,
    downloadReport,
    exporting,
    truncateString,
    formatCpf,
    formatCurrencyInt,
    formatCompetence,
  }
}
